<!-- eslint-disable vue/multi-word-component-names -->
<script lang="ts" setup>
import { ref, computed, onMounted, watch } from "vue";
import { useRoute } from "vue-router";
import { useTasksStore } from "@/stores/v2";
import { storeToRefs } from "pinia";
import { normalizeRouteParam } from "@/utils/v2/routes";
import { TasksService } from "@/services/v2/tasks";
import { useTask } from "@/composables/tasks/useTask";
import { useNotifications } from "@/composables/useNotifications";
import FooterActions from "@/components/v2/Tasks/Vaccination/Buttons/ActionButtons/FooterActions.vue";
import LayoutMain from "@/components/v2/Layouts/LayoutMain.vue";
import { Tank, TaskEnvironmental } from "@/types/v2";

const route = useRoute();
const tasksStore = useTasksStore();
const { activeTask } = storeToRefs(tasksStore);
const { startObserver } = useTask();
const { showTaskCompletionSuccess, showError } = useNotifications();

const isLoading = ref(true);
const error = ref('');
const editingMode = ref(false);
const activeTank = ref<Tank | null>(null);
const formData = ref<Record<string, string>>({});
const selectedUnits = ref<Record<string, string>>({});
const commentsModal = ref(false);
const instructionsModal = ref(false);

const isEnvironmentalTask = (task: any): task is TaskEnvironmental => task?.objective?.id === 'environmental';

const allTanks = computed(() => {
    if (!activeTask.value || !isEnvironmentalTask(activeTask.value)) return [];
    return activeTask.value.details.tanks.map((tank: Tank) => ({
        ...tank,
        status: tank.status || "pending"
    }));
});

const requiredTanks = computed(() => allTanks.value.filter(tank => tank.required));
const optionalTanks = computed(() => allTanks.value.filter(tank => !tank.required));

const attrTemplateData = [
    { name: "temperature", value: "25", label: "Temperature", unit: ["C°"] },
    { name: "saturation", value: "7", label: "O2 saturation", unit: ["%", "mg/L"] },
    { name: "ph", value: "7", label: "pH", unit: ["(no unit)"] },
    { name: "nitrite", value: "3", label: "Nitrite NO2-N", unit: ["(mg/L)"] },
    { name: "ammonia", value: "2", label: "Ammonia NH3", unit: ["(mg/L)"] },
    { name: "salinity", value: "1", label: "Salinity", unit: ["(‰)"] },
];

const getUnits = (key: string): string[] | null => {
    const attribute = attrTemplateData.find((attr) => attr.name === key);
    return attribute ? attribute.unit : null;
};

const getLabel = (key: string): string => {
    const attribute = attrTemplateData.find((attr) => attr.name === key);
    return attribute ? attribute.label : key;
};

const initTask = async () => {
    isLoading.value = true;
    error.value = '';

    const taskKey = normalizeRouteParam(route.params.key);
    if (!taskKey) {
        error.value = 'Invalid task key';
        isLoading.value = false;
        return;
    }

    try {
        tasksStore.resetActiveTaskState();
        const task = await TasksService.startTask(taskKey);
        if (!task) {
            error.value = 'Task not found';
            isLoading.value = false;
            return;
        }
        await tasksStore.setActiveTask(task);
        startObserver();
    } catch (err) {
        error.value = err instanceof Error ? err.message : 'Failed to load task';
    } finally {
        isLoading.value = false;
    }
};

const initFormData = () => {
    formData.value = {};
    selectedUnits.value = {};

    if (!activeTank.value) return;

    if (activeTank.value.requiredAttributes?.length) {
        activeTank.value.requiredAttributes.forEach(attr => {
            // Special case: If the attribute name is 'o2', we need to use 'saturation' in our form
            const formFieldName = attr.name === 'o2' ? 'saturation' : attr.name;

            // Try to find existing measures
            let value = '';
            let unit = '';

            if (activeTank.value?.measures) {
                // First check for the actual field name (o2)
                if (attr.name === 'o2' && activeTank.value.measures.o2) {
                    value = activeTank.value.measures.o2.value || '';
                    unit = activeTank.value.measures.o2.unit || '';
                }
                // Then check for saturation as fallback
                else if (attr.name === 'o2' && activeTank.value.measures.saturation) {
                    value = activeTank.value.measures.saturation.value || '';
                    unit = activeTank.value.measures.saturation.unit || '';
                }
                // Other attributes use normal field names
                else if (activeTank.value.measures[attr.name]) {
                    value = activeTank.value.measures[attr.name].value || '';
                    unit = activeTank.value.measures[attr.name].unit || '';
                }
            }

            formData.value[formFieldName] = value;

            // Set default unit if none found
            if (!unit) {
                const unitOptions = getUnits(formFieldName);
                unit = unitOptions ? unitOptions[0] : '';
            }
            selectedUnits.value[formFieldName] = unit;
        });
    } else {
        attrTemplateData.forEach(attr => {
            formData.value[attr.name] = "";
            selectedUnits.value[attr.name] = attr.unit[0];
        });
    }
};

const setActiveTank = (tank: Tank) => {
    activeTank.value = tank;
    editingMode.value = true;
    initFormData();
};

const handleSave = () => {
    if (!activeTank.value || !activeTask.value || !isEnvironmentalTask(activeTask.value)) return;

    const combinedData: Record<string, { value: string; unit: string }> = {};

    // First get all form values
    for (const key in formData.value) {
        if (Object.prototype.hasOwnProperty.call(formData.value, key)) {
            combinedData[key] = {
                value: formData.value[key],
                unit: selectedUnits.value[key] || "",
            };
        }
    }

    // Map saturation to o2 for API compatibility
    if (combinedData.saturation) {
        combinedData.o2 = {
            value: combinedData.saturation.value,
            unit: combinedData.saturation.unit
        };
    }

    const tank = activeTask.value.details.tanks.find((t: Tank) => t.key === activeTank.value?.key);
    if (tank) {
        // Create a proper measures object with all required fields
        const measures = {
            temperature: combinedData.temperature || { value: '', unit: 'C°' },
            saturation: combinedData.saturation || { value: '', unit: '%' },
            ph: combinedData.ph || { value: '', unit: '(no unit)' },
            nitrite: combinedData.nitrite || { value: '', unit: '(mg/L)' },
            ammonia: combinedData.ammonia || { value: '', unit: '(mg/L)' },
            salinity: combinedData.salinity || { value: '', unit: '(‰)' },
            ...combinedData
        };
        tank.measures = measures;
        tank.status = "Completed";
    }

    editingMode.value = false;
};

const handleBack = () => editingMode.value = false;
const handleTankClick = (tank: Tank) => setActiveTank(tank);
const toggleCommentsModal = () => commentsModal.value = !commentsModal.value;
const toggleInstructionsModal = () => instructionsModal.value = !instructionsModal.value;

const handleCompleteTask = async () => {
    if (!activeTask.value) return;

    if (requiredTanks.value.some(tank => tank.status !== "Completed")) {
        alert("Please complete all required tanks before finishing the task.");
        return;
    }

    try {
        await TasksService.finishTask(activeTask.value);
        showTaskCompletionSuccess(activeTask.value.title, '/dashboard/sites');
    } catch (error) {
        console.error('Error completing environmental task:', error);
        showError("Task Completion Failed", "Failed to complete the environmental task. Please try again.");
    }
};

watch(() => activeTask.value, (newTask) => {
    if (newTask && isEnvironmentalTask(newTask) && newTask.details?.tanks) {
        newTask.details.tanks.forEach((tank: Tank) => {
            if (!tank.status) tank.status = "pending";
        });
    }
}, { immediate: true });

onMounted(initTask);
</script>

<template>
    <LayoutMain>
        <template #mainArea>
            <div v-if="isLoading" class="flex justify-center items-center h-full">
                <div class="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
            </div>

            <div v-else-if="error" class="flex flex-col justify-center items-center h-full">
                <div class="text-red-500 mb-4">{{ error }}</div>
                <button @click="initTask" class="px-4 py-2 bg-primary text-white rounded">Retry</button>
            </div>

            <div v-else class="h-full flex flex-col justify-between">
                <div v-if="!editingMode" class="space-y-6">
                    <div class="flex items-center justify-between border-b border-secondary pb-2">
                        <h1 class="text-2xl font-bold text-gray-800">{{ activeTask?.title }} - Overview</h1>
                        <span v-if="activeTask?.studyTitle" class="text-xs text-white bg-secondary opacity-80 px-2 py-1 rounded font-bold">
                            {{ activeTask.studyTitle }}
                       </span>
                    </div>

                    <div class="space-y-4">
                        <h2 class="font-bold text-xl text-primary mb-2">Required tanks</h2>
                        <div class="space-y-3">
                            <div class="grid grid-cols-12 px-4 py-2 bg-gray-50 rounded-t font-medium text-sm text-gray-600">
                                <div class="col-span-1">#</div>
                                <div class="col-span-5">Tank</div>
                                <div class="col-span-3">Status</div>
                                <div class="col-span-3">Actions</div>
                            </div>

                            <div v-for="(tank, index) in requiredTanks" :key="tank.key"
                                 class="grid grid-cols-12 px-4 py-3 bg-white border border-gray-100 rounded hover:bg-gray-50 cursor-pointer"
                                 @click="handleTankClick(tank)">
                                <div class="col-span-1 text-gray-500">{{ index + 1 }}</div>
                                <div class="col-span-5 font-medium">{{ tank.label }}</div>
                                <div class="col-span-3">
                                 <span class="px-2 py-1 text-xs font-semibold rounded-full"
                                       :class="{
                                     'bg-green-100 text-green-800': tank.status === 'Completed',
                                     'bg-yellow-100 text-yellow-800': tank.status === 'pending'
                                   }">
                                    {{ tank.status === 'Completed' ? 'Completed' : 'Pending' }}
                                 </span>
                                </div>
                                <div class="col-span-3">
                                    <button class="px-3 py-1 text-sm text-white bg-primary rounded hover:bg-primary-dark"
                                            @click.stop="handleTankClick(tank)">
                                        View Details
                                    </button>
                                </div>
                            </div>

                            <div v-if="requiredTanks.length === 0" class="py-4 text-center text-gray-500">
                                No required tanks available.
                            </div>
                        </div>

                        <h2 class="font-bold text-xl text-primary mt-4 mb-2">Optional tanks</h2>
                        <div class="space-y-3">
                            <div class="grid grid-cols-12 px-4 py-2 bg-gray-50 rounded-t font-medium text-sm text-gray-600">
                                <div class="col-span-1">#</div>
                                <div class="col-span-5">Tank</div>
                                <div class="col-span-3">Status</div>
                                <div class="col-span-3">Actions</div>
                            </div>

                            <div v-for="(tank, index) in optionalTanks" :key="tank.key"
                                 class="grid grid-cols-12 px-4 py-3 bg-white border border-gray-100 rounded hover:bg-gray-50 cursor-pointer"
                                 @click="handleTankClick(tank)">
                                <div class="col-span-1 text-gray-500">{{ index + 1 }}</div>
                                <div class="col-span-5 font-medium">{{ tank.label }}</div>
                                <div class="col-span-3">
                                     <span class="px-2 py-1 text-xs font-semibold rounded-full"
                                           :class="{
                                         'bg-green-100 text-green-800': tank.status === 'Completed',
                                         'bg-yellow-100 text-yellow-800': tank.status === 'pending'
                                       }">
                                       {{ tank.status === 'Completed' ? 'Completed' : 'Pending' }}
                                     </span>
                                </div>
                                <div class="col-span-3">
                                    <button class="px-3 py-1 text-sm text-white bg-primary rounded hover:bg-primary-dark"
                                            @click.stop="handleTankClick(tank)">
                                        View Details
                                    </button>
                                </div>
                            </div>

                            <div v-if="optionalTanks.length === 0" class="py-4 text-center text-gray-500">
                                No optional tanks available.
                            </div>
                        </div>
                    </div>

                    <FooterActions>
                        <template #start>
                            <button @click="toggleInstructionsModal" class="w-full">Instructions</button>
                        </template>
                        <template #middle>
                            <button @click="toggleCommentsModal" class="w-full">Comment</button>
                        </template>
                        <template #end>
                            <button @click="handleCompleteTask" class="w-full">Complete task</button>
                        </template>
                    </FooterActions>
                </div>

                <div v-else>
                    <div class="flex mb-5 items-center gap-3 border-b border-secondary">
                        <h1 class="py-2 text-3xl font-bold text-gray-700">{{ activeTank?.label }}</h1>
                    </div>

                    <div v-if="Object.keys(formData).length">
                        <div v-for="(value, key, index) in formData" :key="index" class="mb-5 w-full flex flex-col">
                            <label :for="`input-${index}`" class="text-sm text-primary mb-2">{{ getLabel(key) }}</label>
                            <div class="flex">
                                <input :id="`input-${index}`" v-model="formData[key]" class="w-[90%] border p-2 border-gray-300 text-sm text-gray-600 mb-2"
                                       step="0.01" type="number" />
                                <select v-if="getUnits(key)" v-model="selectedUnits[key]"
                                        class="w-[10%] text-sm p-1 mb-2 text-center bg-primary text-white">
                                    <option v-for="(unit, unitIndex) in getUnits(key)" :key="unitIndex">{{ unit }}</option>
                                </select>
                            </div>
                        </div>
                    </div>

                    <div v-else class="py-8 text-center text-gray-500">
                        No measure requirements found for this tank.
                    </div>

                    <FooterActions>
                        <template #start>
                            <button @click="handleBack" class="w-full">Back</button>
                        </template>
                        <template #end>
                            <button @click="handleSave" class="w-full">Save</button>
                        </template>
                    </FooterActions>
                </div>
            </div>
        </template>
    </LayoutMain>
</template>